import { type ReactNode, useEffect, useRef } from 'react'
import { diffWords } from '#app/utils/word-diff.ts'

/**
 * A KI suggestion waiting for the editor's verdict: nothing in the form
 * changes until it is approved. Gets keyboard focus when it appears, so the
 * verdict is the next thing a keyboard user reaches.
 */
export function SuggestionReview({
	id,
	title,
	children,
	acceptLabel,
	onAccept,
	onReject,
}: {
	id: string
	title: ReactNode
	children: ReactNode
	acceptLabel: string
	onAccept: () => void
	onReject: () => void
}) {
	const ref = useRef<HTMLElement>(null)
	useEffect(() => {
		ref.current?.focus()
	}, [])

	return (
		<section
			ref={ref}
			tabIndex={-1}
			aria-labelledby={`${id}-title`}
			className="border-gold/70 bg-lamp/15 grid gap-3 rounded-xl border p-4 outline-none focus-visible:ring-2 focus-visible:ring-gold"
		>
			<p id={`${id}-title`} className="text-sm font-semibold">
				{title}
			</p>
			<div className="bg-sheet max-h-80 overflow-y-auto rounded-lg p-3 text-sm leading-relaxed whitespace-pre-wrap">
				{children}
			</div>
			<div className="flex flex-wrap gap-2">
				<button
					type="button"
					className="btn btn-primary btn-sm"
					onClick={onAccept}
				>
					{acceptLabel}
				</button>
				<button
					type="button"
					className="btn btn-ghost btn-sm"
					onClick={onReject}
				>
					Forkast
				</button>
			</div>
		</section>
	)
}

/** A paragraph break, made visible where it was added or taken away. */
const showBreaks = (text: string) =>
	/\n/.test(text) && !text.trim() ? text.replace(/\n/g, '¶\n') : text

/** The text with the proofread's changes marked: struck out and added. */
export function WordDiff({ before, after }: { before: string; after: string }) {
	return (
		<>
			{diffWords(before, after).map((part, i) =>
				part.type === 'same' ? (
					<span key={i}>{part.text}</span>
				) : part.type === 'removed' ? (
					<del
						key={i}
						className="bg-error/10 text-error decoration-error/60 rounded-sm"
					>
						<span className="sr-only">Fjerna: </span>
						{showBreaks(part.text)}
					</del>
				) : (
					<ins
						key={i}
						className="bg-success/15 text-success rounded-sm font-medium no-underline"
					>
						<span className="sr-only">Lagt til: </span>
						{showBreaks(part.text)}
					</ins>
				),
			)}
		</>
	)
}

/** How many separate changes a proofread made. */
export const countChanges = (before: string, after: string) => {
	let count = 0
	let inChange = false
	for (const part of diffWords(before, after)) {
		if (part.type === 'same') inChange = false
		else if (!inChange) {
			count++
			inChange = true
		}
	}
	return count
}
